import { c as _c } from "react/compiler-runtime";
// Возврат через API провайдера (fd-order-18, 31.10.2026): состояние заявки
// и действия оператора — «Повторить» и «Вернул вручную». Показывается в
// карточке заказа и в претензии рядом с прежним блоком возврата.
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { formatDateTime, formatPrice } from "@/lib/utils";
import { type PaymentRefund, type PaymentRefundStatus, usePaymentRefundAction, usePaymentRefunds } from "@/pages/payments-api";
const TONE: Record<PaymentRefundStatus, "default" | "secondary" | "destructive" | "outline"> = {
  queued: "secondary",
  processing: "secondary",
  succeeded: "default",
  failed: "destructive",
  uncertain: "destructive",
  manual: "outline"
};
type RefundRow = Pick<PaymentRefund, "id" | "status" | "attempts"> & Partial<Pick<PaymentRefund, "kind" | "amount" | "full" | "providerRequestId" | "lastError" | "nextAttemptAt">>;
export function PaymentRefundRow(t0) {
  const $ = _c(65);
  const {
    refund,
    canManage
  } = t0;
  const {
    t
  } = useTranslation();
  const action = usePaymentRefundAction();
  const [confirmManual, setConfirmManual] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["failed", "uncertain", "queued"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const actionable = t1.includes(refund.status);
  let t2;
  if ($[1] !== t) {
    t2 = t("paymentRefunds.title");
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <span className="font-medium">{t2}</span>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = TONE[refund.status];
  const t5 = `paymentRefunds.status.${refund.status}`;
  let t6;
  if ($[5] !== t || $[6] !== t5) {
    t6 = t(t5);
    $[5] = t;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t4 || $[9] !== t6) {
    t7 = <Badge variant={t4} data-testid="payment-refund-status">{t6}</Badge>;
    $[8] = t4;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== refund.amount || $[12] !== refund.full || $[13] !== refund.kind || $[14] !== t) {
    t8 = typeof refund.amount === "number" && <span className="text-muted-foreground">{formatPrice(refund.amount)}{refund.full != null ? ` · ${t(refund.full ? "paymentRefunds.full" : "paymentRefunds.partial")}` : ""}{refund.kind ? ` · ${t(`paymentRefunds.kind.${refund.kind}`)}` : ""}</span>;
    $[11] = refund.amount;
    $[12] = refund.full;
    $[13] = refund.kind;
    $[14] = t;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t3 || $[17] !== t7 || $[18] !== t8) {
    t9 = <div className="flex flex-wrap items-center gap-2">{t3}{t7}{t8}</div>;
    $[16] = t3;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== refund.attempts || $[21] !== t) {
    t10 = t("paymentRefunds.attempts", {
      count: refund.attempts
    });
    $[20] = refund.attempts;
    $[21] = t;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const t11 = refund.providerRequestId ? ` · ${t("paymentRefunds.request", {
    id: refund.providerRequestId
  })}` : "";
  const t12 = refund.status === "queued" && refund.nextAttemptAt ? ` · ${t("paymentRefunds.nextAttempt", {
    at: formatDateTime(refund.nextAttemptAt)
  })}` : "";
  let t13;
  if ($[23] !== t10 || $[24] !== t11 || $[25] !== t12) {
    t13 = <p className="break-words text-xs text-muted-foreground">{t10}{t11}{t12}</p>;
    $[23] = t10;
    $[24] = t11;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== refund.lastError || $[28] !== refund.status) {
    t14 = refund.lastError && refund.status !== "succeeded" && <p className="break-words text-xs text-destructive">{refund.lastError}</p>;
    $[27] = refund.lastError;
    $[28] = refund.status;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== refund.status || $[31] !== t) {
    t15 = refund.status === "uncertain" && <p className="text-xs text-muted-foreground">{t("paymentRefunds.uncertainHint")}</p>;
    $[30] = refund.status;
    $[31] = t;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== action.error) {
    t16 = action.error && <p className="text-xs text-destructive">{action.error.message}</p>;
    $[33] = action.error;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== action || $[36] !== actionable || $[37] !== canManage || $[38] !== refund.id || $[39] !== t) {
    t17 = canManage && actionable && <div className="flex flex-wrap gap-2 pt-1"><Button size="sm" variant="outline" disabled={action.isPending} onClick={() => action.mutate({
        id: refund.id,
        action: "retry"
      })} data-testid="payment-refund-retry">{t("paymentRefunds.retry")}</Button><Button size="sm" variant="ghost" disabled={action.isPending} onClick={() => setConfirmManual(true)} data-testid="payment-refund-manual">{t("paymentRefunds.manual")}</Button></div>;
    $[35] = action;
    $[36] = actionable;
    $[37] = canManage;
    $[38] = refund.id;
    $[39] = t;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => setConfirmManual(false);
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== t) {
    t19 = t("paymentRefunds.manual");
    $[42] = t;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== t) {
    t20 = t("paymentRefunds.manualConfirm");
    $[44] = t;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== t) {
    t21 = t("paymentRefunds.done");
    $[46] = t;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== action || $[49] !== refund.id) {
    t22 = () => {
      setConfirmManual(false);
      action.mutate({
        id: refund.id,
        action: "manual"
      });
    };
    $[48] = action;
    $[49] = refund.id;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== confirmManual || $[52] !== t19 || $[53] !== t20 || $[54] !== t21 || $[55] !== t22) {
    t23 = <ConfirmDialog open={confirmManual} onClose={t18} title={t19} description={t20} confirmLabel={t21} onConfirm={t22} />;
    $[51] = confirmManual;
    $[52] = t19;
    $[53] = t20;
    $[54] = t21;
    $[55] = t22;
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  let t24;
  if ($[57] !== t13 || $[58] !== t14 || $[59] !== t15 || $[60] !== t16 || $[61] !== t17 || $[62] !== t23 || $[63] !== t9) {
    t24 = <div className="flex flex-col gap-1.5 rounded-md border p-3 text-sm" data-testid="payment-refund">{t9}{t13}{t14}{t15}{t16}{t17}{t23}</div>;
    $[57] = t13;
    $[58] = t14;
    $[59] = t15;
    $[60] = t16;
    $[61] = t17;
    $[62] = t23;
    $[63] = t9;
    $[64] = t24;
  } else {
    t24 = $[64];
  }
  return t24;
}

/** Все возвраты заказа через API: отмена и оплата по старому счёту. */
export function OrderPaymentRefunds(t0) {
  const $ = _c(10);
  const {
    orderId,
    canManage
  } = t0;
  const refunds = usePaymentRefunds(orderId);
  let t1;
  let t2;
  let t3;
  if ($[0] !== canManage || $[1] !== refunds.data?.items) {
    t3 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const items = (refunds.data?.items ?? []).filter(_temp);
      if (items.length === 0) {
        t3 = null;
        break bb0;
      }
      t1 = "flex flex-col gap-2";
      let t4;
      if ($[5] !== canManage) {
        t4 = r_0 => <PaymentRefundRow key={r_0.id} refund={r_0} canManage={canManage} />;
        $[5] = canManage;
        $[6] = t4;
      } else {
        t4 = $[6];
      }
      t2 = items.map(t4);
    }
    $[0] = canManage;
    $[1] = refunds.data?.items;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
  }
  if (t3 !== Symbol.for("react.early_return_sentinel")) {
    return t3;
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t2) {
    t4 = <div className={t1}>{t2}</div>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
function _temp(r) {
  return r.kind !== "return";
}